import { useEffect, useState } from 'react';
import { RankMedal } from '@/art';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import type { RankProgressionPayload } from '@shared/protocol';

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

const TIER_ACCENT: Record<string, { glow: string; rim: string; label: string }> = {
  bronze:     { glow: 'rgba(205,127,50,0.7)', rim: '#cd7f32', label: 'BRONZE' },
  silver:     { glow: 'rgba(220,220,220,0.7)', rim: '#cfcfcf', label: 'SILVER' },
  gold:       { glow: 'rgba(244,199,36,0.8)', rim: '#F4C724', label: 'GOLD' },
  platinum:   { glow: 'rgba(34,211,238,0.8)', rim: '#22D3EE', label: 'PLATINUM' },
  diamond:    { glow: 'rgba(59,130,246,0.8)', rim: '#3B82F6', label: 'DIAMOND' },
  master:     { glow: 'rgba(155,93,229,0.85)', rim: '#9B5DE5', label: 'MASTER' },
  challenger: { glow: 'rgba(244,199,36,0.95)', rim: '#F4C724', label: 'CHALLENGER' },
};

interface Props {
  progression: RankProgressionPayload;
  onDismiss: () => void;
}

/**
 * Full-screen Wild-Rift-style rank promotion / demotion celebration.
 * Auto-dismisses after 4.5s but can be tapped to skip earlier.
 */
export function RankUpModal({ progression, onDismiss }: Props) {
  const t = useT();
  const { after, promoted, demoted } = progression;
  const accent = TIER_ACCENT[after.rank] ?? TIER_ACCENT.bronze!;
  const [stage, setStage] = useState<'in' | 'hold' | 'out'>('in');

  useEffect(() => {
    // Play the promotion/demotion sting on mount — promoted gets the rank-up
    // fanfare, demoted gets the level-up flourish (subdued).
    audio.play(promoted ? 'ui.rank_up' : demoted ? 'ui.error' : 'ui.level_up');
    const t1 = window.setTimeout(() => setStage('hold'), 600);
    const t2 = window.setTimeout(() => setStage('out'), 3800);
    const t3 = window.setTimeout(() => onDismiss(), 4400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onDismiss, promoted, demoted]);

  const title = promoted ? t('rankup.rank_up') : demoted ? t('rankup.demoted') : '';
  const titleColor = promoted ? accent.rim : 'var(--c-red)';

  return (
    <div
      onClick={() => {
        setStage('out');
        window.setTimeout(onDismiss, 400);
      }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(circle at center, ${accent.glow.replace('0.7', '0.3').replace('0.8', '0.35').replace('0.85', '0.4').replace('0.95', '0.45')} 0%, rgba(5,3,20,0.92) 60%, rgba(5,3,20,0.98) 100%)`,
        backdropFilter: 'blur(10px)',
        opacity: stage === 'out' ? 0 : 1,
        transition: 'opacity 0.4s ease',
        overflow: 'hidden',
      }}
    >
      {/* Light beams behind the medal */}
      {promoted && <LightBeams color={accent.rim} />}
      {promoted && <ConfettiBurst color={accent.rim} />}

      <div
        style={{
          textAlign: 'center',
          transform:
            stage === 'in'
              ? 'translateY(40px) scale(0.6)'
              : stage === 'out'
                ? 'translateY(-20px) scale(1.05)'
                : 'translateY(0) scale(1)',
          opacity: stage === 'in' ? 0 : 1,
          transition: 'transform 0.55s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div
          className="display"
          style={{
            fontSize: 11,
            letterSpacing: '0.32em',
            color: 'var(--ink-muted)',
            marginBottom: 10,
          }}
        >
          {promoted ? t('rankup.promotion') : t('rankup.demotion')}
        </div>

        <div
          className="display"
          style={{
            fontSize: 56,
            color: titleColor,
            textShadow: `0 0 30px ${accent.glow}, 0 6px 0 rgba(0,0,0,0.6)`,
            letterSpacing: '0.04em',
            marginBottom: 18,
            animation:
              promoted && stage !== 'out'
                ? 'pulse-scale 1.2s ease-in-out infinite'
                : undefined,
          }}
        >
          {title}
        </div>

        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            marginBottom: 18,
            filter: `drop-shadow(0 0 30px ${accent.glow})`,
          }}
        >
          {/* Spinning halo ring behind medal */}
          {promoted && (
            <div
              style={{
                position: 'absolute',
                inset: -24,
                borderRadius: '50%',
                border: `2px solid ${accent.rim}`,
                borderTopColor: 'transparent',
                borderBottomColor: 'transparent',
                animation: 'spin-slow 3s linear infinite',
                opacity: 0.6,
              }}
            />
          )}
          <RankMedal rank={after.rank} size={160} />
        </div>

        <div
          className="display"
          style={{
            fontSize: 32,
            color: accent.rim,
            letterSpacing: '0.08em',
            textShadow: `0 0 16px ${accent.glow}`,
          }}
        >
          {accent.label} {DIVISION_ROMAN[after.division]}
        </div>

        <div
          style={{
            marginTop: 24,
            fontSize: 11,
            color: 'var(--ink-dim)',
            letterSpacing: '0.18em',
            fontFamily: 'var(--f-display)',
          }}
        >
          {t('rankup.tap_continue')}
        </div>
      </div>
    </div>
  );
}

function LightBeams({ color }: { color: string }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 400 600"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.5,
        mixBlendMode: 'screen',
        pointerEvents: 'none',
        animation: 'spin-slow 18s linear infinite',
        transformOrigin: '50% 50%',
      }}
    >
      <defs>
        <radialGradient id="beam-grad" cx="50%" cy="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      {Array.from({ length: 8 }).map((_, i) => (
        <polygon
          key={i}
          points="200,300 220,-100 180,-100"
          fill="url(#beam-grad)"
          transform={`rotate(${i * 45} 200 300)`}
        />
      ))}
    </svg>
  );
}

function ConfettiBurst({ color }: { color: string }) {
  const pieces = Array.from({ length: 24 });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
      {pieces.map((_, i) => {
        const angle = (i / pieces.length) * Math.PI * 2;
        const dist = 140 + Math.random() * 120;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist - 40;
        const delay = Math.random() * 0.3;
        const palette = ['var(--c-red)', 'var(--c-blue)', 'var(--c-yellow)', 'var(--c-purple)', color];
        const c = palette[i % palette.length];
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 6,
              height: 10,
              borderRadius: 1,
              background: c,
              animation: `confetti-fly 1.4s ${delay}s cubic-bezier(0.2, 0.7, 0.3, 1) forwards`,
              ['--dx' as string]: `${dx}px`,
              ['--dy' as string]: `${dy}px`,
              opacity: 0,
            } as React.CSSProperties}
          />
        );
      })}
    </div>
  );
}
